import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import Icon from './Icon';
import { shortDate, toISODate } from '../lib/dates';
import { useApp, useAppPath } from '../lib/AppContext';

// The 7-day trial while it runs: the "PRO · còn x ngày" chip in the header and, in its last 2 days, a banner on
// Today. Plus the offer's events (shown / "Để sau" / started, each with where: onboarding | first_ai | quota |
// paywall), which Admin › Người dùng › Dùng thử compares.

export const trialDaysLeft = (m) => (m?.tier === 'trial' && m.trial_ends_at
  ? Math.max(1, Math.ceil((new Date(m.trial_ends_at) - Date.now()) / 86400000)) : null);
export const trialEndDay = (m) => shortDate(toISODate(new Date(m.trial_ends_at)));

// Records that a trial offer was shown at `placement`, once each time it becomes `active` (a sheet opening).
export function useTrialOfferShown(placement, active) {
  const { api, setMembership } = useApp();
  const sent = useRef(false);
  useEffect(() => {
    if (!active) {
      sent.current = false;
      return;
    }
    if (sent.current) return;
    sent.current = true;
    api.post('/membership/offer_shown', { placement }).then(setMembership).catch(() => {});
  }, [active, placement, api, setMembership]);
}

export function TrialChip() {
  const { membership, editable } = useApp();
  const path = useAppPath();
  const days = trialDaysLeft(membership);
  if (!editable || !days) return null;
  return (
    <Link to={path('/credits')} aria-label={`Pro dùng thử, còn ${days} ngày`}
      className="whitespace-nowrap rounded-full bg-[#f4c026] px-2.5 py-1 text-[11px] font-extrabold text-[#17201b]">
      PRO · còn {days} ngày
    </Link>
  );
}

const seenKey = (m) => `trial-ending-seen:${m.trial_ends_at}`;

// "Pro dùng thử còn 2 ngày" on Today, until closed (once per trial).
export function TrialEndingBanner() {
  const { membership, openPaywall } = useApp();
  const days = trialDaysLeft(membership);
  const [closed, setClosed] = useState(() => {
    try {
      return !!membership?.trial_ends_at && window.localStorage.getItem(seenKey(membership)) === '1';
    } catch {
      return false;
    }
  });
  if (!days || days > 2 || closed) return null;
  const close = () => {
    setClosed(true);
    try { window.localStorage.setItem(seenKey(membership), '1'); } catch { /* shown again next time */ }
  };
  return (
    <div className="flex items-start gap-3 rounded-[16px] bg-[#15261f] p-4 text-[#e8f3ed]">
      <span className="grid size-8 shrink-0 place-items-center rounded-full bg-[#6fd3a0] text-sm font-extrabold text-[#15261f]">{days}</span>
      <div className="min-w-0 flex-1">
        <p className="font-bold">Pro dùng thử còn {days} ngày</p>
        <p className="text-sm text-[#a9c2b5]">Kết thúc {trialEndDay(membership)}, sau đó tự về bản Free, không trừ tiền.</p>
        <button type="button" className="mt-1 text-sm font-bold text-[#6fd3a0]" onClick={() => openPaywall('upgrade')}>Xem gói Pro ›</button>
      </div>
      <button type="button" aria-label="Đóng" className="-m-2 grid size-11 place-items-center text-[#a9c2b5]" onClick={close}>
        <Icon name="x" className="size-5" />
      </button>
    </div>
  );
}
